 
 
import { useState, useMemo, useRef, useEffect, memo } from 'react';
import { useTheme } from '@mui/material';
import { makeStyles, useMediaQuery } from '@ellucian/react-design-system/core';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { CONSTANTS } from '../Helpers/Configuration';
import useApplicationForm from './useApplicationForm';
import '../Admin/AdminControls/FormReactQuill.css';
import { spacing60, spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import { Typography, Divider, Popper, IconButton } from '@ellucian/react-design-system/core';
import { FormattedMessage } from 'react-intl';
import SectionLabel from './SectionLabel';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';
import RepeatingSection from './RepeatingSection';
import QuestionGroups from './QuestionGroups';
import ConditionalLogicCheck from './ConditionalLogicCheck';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';

const useStyles = makeStyles({
    name: 'ApplicationFormPage'
})(theme => ({
    hide: {
        display: 'none',
    },
    helpButton: {
        marginLeft: spacing40,
    },
    srOnly: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        width: 1,
        whiteSpace: 'nowrap',
    },
    pageDivider: {
        marginBottom: [0, '!important'],
    },
    pageHeading: {
        display: 'flex',
        alignItems: 'baseline',
        paddingTop: spacing60,
        '&.short': {
            paddingTop: spacing40,
        },
        '&.noGap': {
            paddingTop: 'unset',
        },
    },
    sectionHeading: {
        display: 'flex',
        alignItems: 'baseline',
        paddingTop: spacing60,
        '&.short': {
            paddingTop: spacing40,
        },
    },
    horizontal: {
        [theme.breakpoints.down('md')]: {
            gridAutoFlow: 'row',
        },
    },
    formPage: {
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        '& .MuiFormLabel-asterisk': {
            position: 'static !important',
        },
    },
}));
const ApplicationFormPage = props => {
    const intl = useIntl();
    const {
        page,
    } = props;
    const { inquiryMode, supplementalFormMode } = useApplicationForm();
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const pageTitleRef = useRef(null);
    const routeParams = useParams();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [openPageHelp, setOpenPageHelp] = useState(false);
    const [pageHelpAnchorEl, setPageHelpAnchorEl] = useState(null);
    const [openSectionHelp, setOpenSectionHelp] = useState(false);
    const [sectionHelpAnchorEl, setSectionHelpAnchorEl] = useState(null);
    const [pageLiveMessage, setPageLiveMessage] = useState('');
    const pageReadTimerRef = useRef(null);
    const [sectionLiveMessage, setSectionLiveMessage] = useState('');
    const [sectionLiveText, setSectionLiveText] = useState('');
    const sectionReadTimerRef = useRef(null);

    const pageHelpId = page?.id ? `page-help-${page.id}` : 'page-help';
    const pageHelpText = page?.helpId ? intl.formatMessage({ id: page.helpId }) : '';

    const handlePageHelpClick = event => {
        const { currentTarget } = event;
        setPageHelpAnchorEl(currentTarget);
        setOpenPageHelp(prev => !prev);
    };
    const handleSectionHelpClick = (event, text) => {
        const { currentTarget } = event;
        setSectionHelpAnchorEl(currentTarget);
        setSectionLiveText(text || '');
        setOpenSectionHelp(prev => !prev);
    };

    useEffect(() => {
        if (openPageHelp) {
            clearTimeout(pageReadTimerRef.current);
            pageReadTimerRef.current = setTimeout(() => setPageLiveMessage(pageHelpText), 400);
        } else {
            clearTimeout(pageReadTimerRef.current);
            setPageLiveMessage('');
        }
        return () => clearTimeout(pageReadTimerRef.current);
    }, [openPageHelp, pageHelpText]);

    useEffect(() => {
        if (openSectionHelp && sectionLiveText) {
            clearTimeout(sectionReadTimerRef.current);
            sectionReadTimerRef.current = setTimeout(() => setSectionLiveMessage(sectionLiveText), 400);
        } else {
            clearTimeout(sectionReadTimerRef.current);
            setSectionLiveMessage('');
        }
        return () => clearTimeout(sectionReadTimerRef.current);
    }, [openSectionHelp, sectionLiveText]);
    const rootClassName = cx(classes.formPage, {
        [classes.hide]: !(supplementalFormMode || routeParams.pageId === page.id),
    });
    const pageHeadingClassName = useMemo(() => {
        return cx(classes.pageHeading, {
            short: inquiryMode,
            noGap: supplementalFormMode,
        }, [classes, inquiryMode, supplementalFormMode]);
    });
    const sectionHeadingClassName = useMemo(() => {
        return cx(classes.sectionHeading, {
            short: inquiryMode || supplementalFormMode,
        }, [classes, inquiryMode, supplementalFormMode]);
    });

    // Ensure keyboard focus moves to the top of the newly rendered page (page title) when navigating between pages.
    useEffect(() => {
        if ((routeParams.pageId === page.id) && pageTitleRef.current) {
            pageTitleRef.current.focus();
        }
    }, [routeParams.pageId, page.id]);

    return (
        <div className={rootClassName}>
            <main id='application-form-page'>
                <div>
                    <div className={pageHeadingClassName}>
                        <Typography 
                            variant="h1" 
                            id="page-title" 
                            ref={pageTitleRef} tabIndex="-1"
                        >
                            {intl.formatMessage({ id: page.titleId })}
                        </Typography>
                        {page.showHelp && page.helpId &&
                        <>
                            <IconButton
                                className={classes.helpButton}
                                aria-label={intl.formatMessage({ id: 'helpText' })}
                                aria-describedby={pageHelpId}
                                color="gray"
                                onClick={handlePageHelpClick}
                                onFocus={event => { setPageHelpAnchorEl(event.currentTarget); setOpenPageHelp(true); }}
                                onBlur={() => setOpenPageHelp(false)}
                            >
                                <Icon name="help" />
                            </IconButton>
                            <Popper
                                anchorEl={pageHelpAnchorEl}
                                arrow
                                onClose={() => setOpenPageHelp(false)}
                                open={openPageHelp}
                                placement={isMobile ? 'bottom' : 'right'}
                                text={intl.formatMessage({ id: page.helpId })}
                                transition={false}
                            />
                            <div className={classes.srOnly} aria-live="polite">{openPageHelp ? pageLiveMessage : ''}</div>
                        </>
                        }
                    </div>
                    <div>
                        {
                            page.showDescription && page.descriptionId &&
                        <div><HtmlFormattedMessage descriptor={{ id: page.descriptionId }} stripHeadingTags={inquiryMode} normalizeSpaces /></div>
                        }
                    </div>
                    {!inquiryMode &&
                    <Divider className={classes.pageDivider} />
                    }
                </div>
                {page.sections?.map((it, idx) => (
                    <ConditionalLogicCheck key={idx}
                        conditions={it.conditions}
                        content={it}
                        contentType={CONSTANTS.appTemplateControlTypes.section}
                        pageId={page.id}
                    >
                        <div>
                            <Typography component="div">
                                <div className={sectionHeadingClassName}>
                                    {it.showTitle && it.titleId &&
                                    <>
                                        <SectionLabel>
                                            <FormattedMessage id={it.titleId} />
                                        </SectionLabel>
                                    </>
                                    }
                                    {it.showHelp && it.helpId &&
                                    <>
                                        <IconButton
                                            className={classes.helpButton}
                                            aria-label={intl.formatMessage({ id: 'helpText' })}
                                            aria-describedby={`section-help-${page.id || ''}-${it.id}`}
                                            color="gray"
                                            onClick={event => handleSectionHelpClick(event, intl.formatMessage({ id: it.helpId }))}
                                            onFocus={event => { setSectionHelpAnchorEl(event.currentTarget); setOpenSectionHelp(true); setSectionLiveText(intl.formatMessage({ id: it.helpId })); }}
                                            onBlur={() => setOpenSectionHelp(false)}
                                        >
                                            <Icon name="help" />
                                        </IconButton>
                                        <Popper
                                            anchorEl={sectionHelpAnchorEl}
                                            arrow
                                            onClose={() => setOpenSectionHelp(false)}
                                            open={openSectionHelp}
                                            placement={isMobile ? 'bottom' : 'right'}
                                            text={intl.formatMessage({ id: it.helpId })}
                                            transition={false}
                                        />
                                        <div className={classes.srOnly} aria-live="polite">{openSectionHelp ? sectionLiveMessage : ''}</div>
                                    </>
                                    }
                                </div>
                                {
                                    it.showDescription && it.descriptionId && it.descriptionTop === 'true' &&
                                <div><HtmlFormattedMessage descriptor={{ id: it.descriptionId }} stripHeadingTags={inquiryMode} normalizeSpaces /></div>
                                }
                                {!it.showRepetition &&
                                <QuestionGroups
                                    pageId={page.id}
                                    groups={page.sections[idx].groups}
                                    prefix={page.id}
                                />
                                }
                                {it.showRepetition &&
                                <RepeatingSection name={`${page.id}.${it.id}`} section={it}>
                                    {(name) => {
                                        return (
                                            <QuestionGroups
                                                groups={page.sections[idx].groups}
                                                prefix={name}
                                                repeatingSection={true}
                                                pageId={page.id}
                                                sectionId={it.id}
                                            />
                                        );
                                    }}
                                </RepeatingSection>
                                }
                                {
                                    it.showDescription && it.descriptionId && it.descriptionTop === 'false' &&
                                <div><HtmlFormattedMessage descriptor={{ id: it.descriptionId }} stripHeadingTags={inquiryMode} normalizeSpaces /></div>
                                }
                            </Typography>
                        </div>
                    </ConditionalLogicCheck>
                ))}
            </main>
        </div>
    );
};
export default memo(ApplicationFormPage);
